import { brandColor, categoryIcon } from "@/lib/brandCatalog";

/**
 * Unique branded product tile — renders as a self-contained SVG-style card.
 * Every product yields a visually unique tile thanks to brand color + category
 * icon + model-number typography + deterministic circuit pattern seeded from the id.
 */
export default function ProductImage({ product, size = "md" }) {
    const color = brandColor(product.brand);
    const Icon = categoryIcon(product.category);

    // Deterministic pattern seed derived from product id
    const seed = (product.id || product.model_number || product.name || "0")
        .split("")
        .reduce((a, c) => a + c.charCodeAt(0), 0);
    const rot = seed % 8;
    const grid = 6 + (seed % 4);

    const dots = [];
    for (let x = 0; x < grid; x++) {
        for (let y = 0; y < grid; y++) {
            const on = ((x * 7 + y * 3 + seed) % 5) === 0;
            if (on) dots.push([x, y]);
        }
    }

    return (
        <div className="relative w-full h-full overflow-hidden bg-navy">
            {/* Base gradient — solid dark to brand color */}
            <div
                className="absolute inset-0"
                style={{
                    background: `linear-gradient(135deg, ${color} 0%, #0A2540 65%, #061729 100%)`,
                }}
            />

            {/* Circuit-pattern layer */}
            <svg
                viewBox="0 0 200 200"
                preserveAspectRatio="none"
                className="absolute inset-0 w-full h-full opacity-25"
                aria-hidden="true"
                style={{ transform: `rotate(${rot * 5}deg)` }}
            >
                <defs>
                    <pattern id={`p-${seed}`} width="20" height="20" patternUnits="userSpaceOnUse">
                        <path d="M0 10 H20 M10 0 V20" stroke="white" strokeWidth="0.4" opacity="0.35" />
                        <circle cx="10" cy="10" r="1" fill="white" opacity="0.6" />
                    </pattern>
                </defs>
                <rect width="200" height="200" fill={`url(#p-${seed})`} />
                {dots.map(([x, y], i) => (
                    <circle
                        key={i}
                        cx={x * (200 / grid) + 100 / grid}
                        cy={y * (200 / grid) + 100 / grid}
                        r={1.6}
                        fill="white"
                        opacity="0.65"
                    />
                ))}
            </svg>

            {/* Glow accent */}
            <div
                className="absolute -top-1/4 -right-1/4 w-2/3 h-2/3 rounded-full blur-3xl opacity-40"
                style={{ background: color }}
            />

            {/* Content overlay */}
            <div className="absolute inset-0 p-5 flex flex-col justify-between text-white">
                <div className="flex justify-between items-start gap-2">
                    <span className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest bg-white/15 backdrop-blur-sm px-2.5 py-1 rounded-full border border-white/20">
                        {product.category}
                    </span>
                    <div className="w-9 h-9 rounded-lg bg-white/15 backdrop-blur-sm border border-white/20 flex items-center justify-center">
                        <Icon className="w-4 h-4 text-white" />
                    </div>
                </div>

                <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-80">
                        {product.brand}
                    </div>
                    <div className={`mt-1 font-display font-extrabold leading-tight ${size === "lg" ? "text-2xl" : "text-lg"}`}>
                        {product.model_number}
                    </div>
                </div>
            </div>
        </div>
    );
}
